import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import axios from 'axios';
import { url } from '../../../../../env';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(value || 0);
};

export const InvoiceComparisonPanel = ({ fileData, budgetData, onRecotizarClick, activeAdjustmentDraft, hasAnyActiveDraft, onGoToApproval, onDiscardDraft }) => {
    const analysis = fileData?.analysis;
    const comparison = fileData?.comparison;

    // If no analysis data or file is not XML, don't render
    if (!analysis) {
        return null;
    }

    // Invalid XML
    if (analysis.status === 'invalid' || analysis.status === 'error') {
        return (
            <div className="mt-2 mb-3 p-3 border-1 border-orange-200 bg-orange-50 border-round">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-exclamation-triangle text-orange-500"></i>
                    <span className="text-orange-800 font-medium">
                        {analysis.error || 'No se pudo analizar el archivo XML'}
                    </span>
                </div>
            </div>
        );
    }

    // Valid XML
    const parsedXml = analysis.parsed_xml;
    if (!parsedXml || !comparison) {
        return null;
    }

    const { price_changed, vehicle_changed, can_requote } = comparison;
    const hasChanges = price_changed || vehicle_changed;

    return (
        <div className="mt-2 mb-3 border-1 border-round overflow-hidden"
            style={{
                borderColor: !hasChanges ? '#22c55e' : (vehicle_changed ? '#ef4444' : '#f59e0b'),
            }}
        >
            {/* Header */}
            <div className="p-3" style={{
                backgroundColor: !hasChanges ? '#f0fdf4' : (vehicle_changed ? '#fef2f2' : '#fffbeb'),
            }}>
                <div className="flex align-items-center justify-content-between">
                    <div className="flex align-items-center gap-2">
                        <i className={`pi ${!hasChanges ? 'pi-check-circle text-green-500' : 'pi-info-circle'}`}
                            style={{ color: hasChanges ? (vehicle_changed ? '#ef4444' : '#f59e0b') : undefined }}
                        ></i>
                        <span className="font-bold" style={{
                            color: !hasChanges ? '#166534' : (vehicle_changed ? '#991b1b' : '#92400e')
                        }}>
                            {!hasChanges
                                ? 'Coincide con la cotizacion'
                                : vehicle_changed
                                    ? 'Cambio de vehiculo detectado'
                                    : 'Diferencia de precio detectada'
                            }
                        </span>
                    </div>
                    {parsedXml.folio && (
                        <Tag value={`Folio: ${parsedXml.serie || ''}${parsedXml.folio}`} severity="info" />
                    )}
                </div>
            </div>

            {/* XML Summary */}
            <div className="p-3 border-bottom-1 border-gray-200">
                <div className="grid">
                    <div className="col-6 md:col-3">
                        <small className="text-gray-500 block">Emisor</small>
                        <span className="font-medium text-sm">{parsedXml.emisor?.nombre || 'N/A'}</span>
                    </div>
                    <div className="col-6 md:col-3">
                        <small className="text-gray-500 block">RFC Emisor</small>
                        <span className="font-medium text-sm">{parsedXml.emisor?.rfc || 'N/A'}</span>
                    </div>
                    <div className="col-6 md:col-3">
                        <small className="text-gray-500 block">Total Factura</small>
                        <span className="font-bold text-sm">{formatCurrency(parsedXml.total)}</span>
                    </div>
                    <div className="col-6 md:col-3">
                        <small className="text-gray-500 block">Fecha</small>
                        <span className="font-medium text-sm">
                            {parsedXml.fecha ? new Date(parsedXml.fecha).toLocaleDateString('es-MX') : 'N/A'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Price Comparison */}
            {comparison.price && (
                <div className="p-3 border-bottom-1 border-gray-200">
                    <div className="flex align-items-center justify-content-between">
                        <div>
                            <small className="text-gray-500 block mb-1">Precio</small>
                            <div className="flex align-items-center gap-3">
                                <span className="text-sm">
                                    <span className="text-gray-500">Cotizacion: </span>
                                    <span className="font-medium">{formatCurrency(comparison.price.budget)}</span>
                                </span>
                                <i className="pi pi-arrow-right text-gray-400 text-xs"></i>
                                <span className="text-sm">
                                    <span className="text-gray-500">Factura: </span>
                                    <span className="font-bold">{formatCurrency(comparison.price.xml)}</span>
                                </span>
                            </div>
                        </div>
                        {price_changed && (
                            <Tag
                                value={`${comparison.price.diff > 0 ? '+' : ''}${formatCurrency(comparison.price.diff)} (${comparison.price.diff_pct}%)`}
                                severity={comparison.price.diff > 0 ? 'danger' : 'success'}
                            />
                        )}
                        {!price_changed && <Tag value="Coincide" severity="success" />}
                    </div>
                </div>
            )}

            {/* Vehicle Comparison */}
            {comparison.vehicle && comparison.vehicle.match_level !== 'not_comparable' && (
                <div className="p-3 border-bottom-1 border-gray-200">
                    <small className="text-gray-500 block mb-1">Vehiculo</small>
                    {comparison.vehicle.match_level === 'exact' ? (
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-check text-green-500"></i>
                            <span className="text-sm text-green-700">Vehiculo coincide con la cotizacion</span>
                        </div>
                    ) : (
                        <div>
                            {comparison.vehicle.mismatched_fields_detail?.map((detail, idx) => (
                                <div key={idx} className="text-sm text-orange-800 mb-1">
                                    <i className="pi pi-exclamation-circle text-orange-500 mr-1"></i>
                                    {detail}
                                </div>
                            ))}
                            {comparison.vehicle.xml_description && (
                                <div className="mt-2 text-xs text-gray-500">
                                    Descripcion factura: {comparison.vehicle.xml_description}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {comparison.vehicle?.match_level === 'not_comparable' && (
                <div className="p-3 border-bottom-1 border-gray-200">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-info-circle text-blue-500"></i>
                        <span className="text-sm text-blue-700">
                            No se pudieron extraer datos del vehiculo de la factura. Verificacion manual requerida.
                        </span>
                    </div>
                </div>
            )}

            {/* RFC Validation */}
            {comparison.receptor && !comparison.receptor.rfc_matches && (
                <div className="p-3 border-bottom-1 border-gray-200">
                    <Message
                        severity="warn"
                        text={`RFC receptor (${comparison.receptor.rfc}) no coincide con el esperado (${comparison.receptor.expected_rfc}). Verificar que la factura esta a nombre correcto.`}
                        className="w-full"
                    />
                </div>
            )}

            {/* Warnings */}
            {comparison.warnings?.length > 0 && (
                <div className="p-3 border-bottom-1 border-gray-200">
                    {comparison.warnings.map((warning, idx) => (
                        <div key={idx} className="flex align-items-center gap-2 mb-1">
                            <i className="pi pi-exclamation-triangle text-orange-500"></i>
                            <span className="text-sm text-orange-800">{warning}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Recotizar CTA — only when no draft exists for this file */}
            {can_requote && !activeAdjustmentDraft && (
                <div className="p-3 flex align-items-center justify-content-end gap-2">
                    {hasAnyActiveDraft && (
                        <span className="text-xs text-orange-700">
                            <i className="pi pi-lock text-xs mr-1"></i>
                            Ya hay una recotización en proceso
                        </span>
                    )}
                    <Button
                        label="Recotizar con factura"
                        icon="pi pi-calculator"
                        severity={vehicle_changed ? 'danger' : 'warning'}
                        outlined
                        disabled={hasAnyActiveDraft}
                        onClick={() => onRecotizarClick?.({
                            comparisonData: comparison,
                            analysisData: analysis,
                            fileId: fileData.file_id || fileData.id,
                        })}
                    />
                </div>
            )}

            {/* Draft in progress (draft / ready_for_review) */}
            {activeAdjustmentDraft && ['draft', 'ready_for_review'].includes(activeAdjustmentDraft.status) && (
                <div className="p-3" style={{ background: '#fffbeb', borderTop: '1px solid #f59e0b' }}>
                    <div className="flex align-items-center justify-content-between">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-spin pi-spinner" style={{ color: '#d97706' }}></i>
                            <span className="font-bold text-sm" style={{ color: '#92400e' }}>
                                Recotización en proceso
                            </span>
                        </div>
                        <Button
                            label="Ir a aprobación"
                            icon="pi pi-arrow-right"
                            severity="warning"
                            size="small"
                            onClick={() => onGoToApproval?.(activeAdjustmentDraft)}
                        />
                    </div>
                    <p className="text-xs m-0 mt-2" style={{ color: '#78350f' }}>
                        La cotización fue modificada. Pendiente de re-aprobación del cliente.
                    </p>
                </div>
            )}

            {/* Draft completed (approved) */}
            {activeAdjustmentDraft && activeAdjustmentDraft.status === 'approved' && (
                <div className="p-3" style={{ background: '#f0fdf4', borderTop: '1px solid #22c55e' }}>
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-check-circle" style={{ color: '#16a34a' }}></i>
                        <span className="font-bold text-sm" style={{ color: '#166534' }}>
                            Recotización completada
                        </span>
                    </div>
                    <p className="text-xs m-0 mt-1" style={{ color: '#15803d' }}>
                        La cotización fue ajustada y aprobada exitosamente.
                    </p>
                </div>
            )}
        </div>
    );
};
